import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Trophy, Medal, Star, Award, Gift, Calendar, DollarSign,
  Loader2, ChevronRight, Sparkles, Download, FileSpreadsheet
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { useLanguage } from '../context/LanguageContext';
import { API_URL } from '../utils/apiConfig';
import { toast } from 'sonner';

export default function RewardHistory({ token, isOpen, onClose }) {
  const [rewards, setRewards] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  const { language } = useLanguage();
  
  const t = {
    en: {
      title: 'Your Rewards',
      subtitle: 'Leaderboard achievements and bonuses',
      totalEarned: 'Total Earned',
      achievements: 'Achievements',
      rank: 'Rank',
      month: 'Month',
      bonus: 'Bonus',
      referrals: 'Referrals',
      noRewards: 'No rewards yet',
      noRewardsDesc: 'Keep referring friends to earn monthly bonuses!',
      badge: 'Badge',
      credited: 'Credited',
      pending: 'Pending',
      export: 'Export',
      exportCSV: 'Download CSV',
      exportJSON: 'Download JSON',
      exportSuccess: 'Rewards exported successfully',
      exportError: 'Failed to export rewards'
    },
    es: {
      title: 'Tus Recompensas',
      subtitle: 'Logros y bonificaciones del ranking',
      totalEarned: 'Total Ganado',
      achievements: 'Logros',
      rank: 'Posición',
      month: 'Mes',
      bonus: 'Bonificación',
      referrals: 'Referidos',
      noRewards: 'Sin recompensas aún',
      noRewardsDesc: '¡Sigue refiriendo amigos para ganar bonificaciones mensuales!',
      badge: 'Insignia',
      credited: 'Acreditado',
      pending: 'Pendiente',
      export: 'Exportar',
      exportCSV: 'Descargar CSV',
      exportJSON: 'Descargar JSON',
      exportSuccess: 'Recompensas exportadas exitosamente',
      exportError: 'Error al exportar recompensas'
    }
  };
  
  const text = t[language] || t.en;
  
  useEffect(() => {
    if (isOpen && token) {
      fetchRewards();
    }
  }, [isOpen, token]);
  
  const fetchRewards = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/referrals/my-rewards`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRewards(data.rewards || []);
        setStats(data.stats || null);
      }
    } catch (error) {
      console.error('Failed to fetch rewards:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/referrals/my-rewards/export?format=${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pecash_rewards_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(text.exportSuccess);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(text.exportError);
    } finally {
      setExporting(false);
    }
  };
  
  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-slate-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <Award className="w-6 h-6 text-purple-500" />;
  };
  
  const getRankBadgeStyle = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white';
    if (rank === 2) return 'bg-gradient-to-r from-slate-300 to-slate-400 text-slate-800';
    if (rank === 3) return 'bg-gradient-to-r from-amber-500 to-orange-500 text-white';
    return 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white';
  };
  
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };
  
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              {text.title}
            </DialogTitle>
            {rewards.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExport('csv')}
                  disabled={exporting}
                  className="text-xs"
                  data-testid="export-csv-btn"
                >
                  {exporting ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <FileSpreadsheet className="w-3 h-3 mr-1" />
                  )}
                  CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExport('json')}
                  disabled={exporting}
                  className="text-xs"
                  data-testid="export-json-btn"
                >
                  {exporting ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <Download className="w-3 h-3 mr-1" />
                  )}
                  JSON
                </Button>
              </div>
            )}
          </div>
          <DialogDescription>{text.subtitle}</DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Stats */}
            {stats && (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm text-emerald-700">{text.totalEarned}</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-800">
                    ${stats.total_earned?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-4 h-4 text-purple-600" />
                    <span className="text-sm text-purple-700">{text.achievements}</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-800">
                    {stats.total_achievements || 0}
                  </p>
                </div>
              </div>
            )}
            
            {/* Rewards List */}
            {rewards.length > 0 ? (
              <div className="space-y-3">
                {rewards.map((reward, i) => (
                  <motion.div
                    key={reward.id || i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getRankBadgeStyle(reward.rank)}`}>
                            {getRankIcon(reward.rank)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-900">#{reward.rank}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                reward.credited 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {reward.credited ? text.credited : text.pending}
                              </span>
                            </div>
                            <p className="text-sm font-medium text-purple-600">{reward.badge}</p>
                            <p className="text-xs text-slate-500">
                              {reward.referrals_count} {text.referrals} • {formatMonth(reward.month)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-emerald-600">
                            +${reward.bonus_usd?.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{text.noRewards}</h3>
                <p className="text-sm text-slate-500">{text.noRewardsDesc}</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
